import { ref, computed } from 'vue';
import { getAnalysisContentAPI, postAnalysisLogAPI } from '@/api/AnalysisApi';
import { usePaymentsStore } from '@/store/modules/payments';

/** 낭만작명 analysisTypeId — 16개 모드 공용 */
export const NANGMAN_NAMING_TYPE_ID = 47;

/** 낭만작명 컨텐츠 평가(/rate) serviceName — 백엔드 contract */
export const NANGMAN_NAMING_SERVICE_NAME = 'NANGMAN_NAMING';

/**
 * 낭만작명 결제 로직 (PC/Mobile 공용).
 *
 * - analysisTypeData 1회 조회 후 가격/할인 정보 제공
 * - 보유 포인트/분석권 vs 필요 포인트 비교
 * - 결제 수단(point/ticket) 결정 후 postAnalysisLogAPI 호출
 * - extraJson.modeKey, extraJson.detailInput 포함 (서버 contract)
 */
export const useNamingPayment = (selectionRef = null) => {
  const paymentsStore = usePaymentsStore();

  const analysisTypeData = ref(null);
  const isPaymentProcessing = ref(false);

  const fetchAnalysisType = async () => {
    if (analysisTypeData.value) return analysisTypeData.value;
    try {
      const data = await getAnalysisContentAPI({ analysisTypeId: NANGMAN_NAMING_TYPE_ID });
      analysisTypeData.value = data;
      return data;
    } catch (e) {
      return null;
    }
  };

  /**
   * 현재 선택(modeKey)에 매칭되는 옵션 찾기.
   * 추천개수는 5개 고정이라 modeKey당 옵션이 1개 → modeKey 매칭만으로 충분.
   * @param {{type,target,detail}} sel
   */
  const findOption = (sel) => {
    const opts = analysisTypeData.value?.options;
    if (!opts?.length || !sel?.type || !sel?.target || !sel?.detail) return null;
    const modeKey = `${sel.type}-${sel.target}-${sel.detail}`;
    return opts.find((o) => o.modeKey === modeKey) ?? null;
  };

  /** 현재 선택에 매칭된 옵션 (selectionRef 기반, 가격/티켓 표시용) */
  const matchedOption = computed(() => findOption(selectionRef?.value));

  /** 결제가: 매칭 옵션 우선, 옵션 없으면 단일가 fallback */
  const pointPrice = computed(
    () => matchedOption.value?.pointPrice ?? analysisTypeData.value?.pointPrice ?? 0,
  );
  const pointBasePrice = computed(
    () =>
      matchedOption.value?.pointBasePrice ??
      analysisTypeData.value?.pointBasePrice ??
      pointPrice.value,
  );

  const currentPoints = computed(() => {
    const formatted = paymentsStore?.formattedPoints || '0';
    const raw = typeof formatted === 'string' ? formatted.replace(/,/g, '') : formatted;
    return Number(raw) || 0;
  });

  /** 보유 분석권 수: 옵션별 보유수 우선, 옵션 매칭 전이면 단일 총합 fallback */
  const namingTicketCount = computed(() => {
    const opt = matchedOption.value;
    if (opt) return paymentsStore.getNamingTicketCount(opt) ?? 0;
    return paymentsStore?.namingTicketCount ?? 0;
  });

  const isPointActive = computed(
    () => pointPrice.value > 0 && currentPoints.value >= pointPrice.value,
  );
  const isTicketActive = computed(() => namingTicketCount.value > 0);

  /** PopAnalysisInfo에 전달할 analysisType 객체 */
  const popAnalysisType = computed(() => ({
    analysisTypeId: NANGMAN_NAMING_TYPE_ID,
    pointPrice: pointPrice.value,
    pointBasePrice: pointBasePrice.value,
    name: matchedOption.value?.name ?? '낭만작명',
    // 분석권 업셀 카드가 연결할 서버 지정 추천 상품 — 옵션별 지정 우선, 타입 공통값 fallback
    recommendedProductId:
      matchedOption.value?.recommendedProductId ??
      analysisTypeData.value?.recommendedProductId ??
      null,
    // 추천 상품의 옵션이 모드 선택지인 경우 현재 모드 가격만 표시하기 위한 키
    modeKey: matchedOption.value?.modeKey ?? null,
  }));

  /**
   * 결제 API 호출.
   *
   * @param {object} payload
   * @param {string} payload.type     - 'recommend' | 'analyze'
   * @param {string} payload.target   - 'person' | 'pet' | 'biz' | 'culture'
   * @param {string} payload.detail   - 'kor' | 'han' | 'animal' | ...
   * @param {object} payload.detailInput - 사용자 입력값 (현재 모드 필드만)
   * @param {object} payload.saju    - 작명 기준 사주 ({ guestUserId, ... })
   * @param {'point'|'ticket'} payload.paymentMethod
   */
  const submitPayment = async ({ type, target, detail, detailInput, saju, paymentMethod }) => {
    if (isPaymentProcessing.value) return null;
    isPaymentProcessing.value = true;
    try {
      const modeKey = `${type}-${target}-${detail}`;
      const option = findOption({ type, target, detail });
      // 추천개수 선택 UI 제거 — recommend 모드는 5개 고정값을 서버에 전달
      const finalDetailInput = { ...(detailInput || {}) };
      if (type === 'recommend' && finalDetailInput.recommendCount == null) {
        finalDetailInput.recommendCount = 5;
      }
      const params = {
        guestUserId: saju?.guestUserId,
        analysisTypeId: NANGMAN_NAMING_TYPE_ID,
        paymentMethod: paymentMethod === 'ticket' ? 'ticket' : 'point',
        extraJson: {
          modeKey,
          detailInput: finalDetailInput,
        },
      };

      // 옵션별 결제 — 서버가 optionId로 가격/분석권 종류를 판별 (포인트·티켓 공통)
      if (option?.optionId != null) params.optionId = option.optionId;

      const result = await postAnalysisLogAPI(params);

      // 분석권 결제 성공 시 서버 반영 지연 대비 낙관적 차감 (옵션별)
      if (paymentMethod === 'ticket') {
        paymentsStore.decrementNamingTicket(option);
      }

      return result;
    } finally {
      isPaymentProcessing.value = false;
    }
  };

  return {
    NANGMAN_NAMING_TYPE_ID,
    analysisTypeData,
    matchedOption,
    isPaymentProcessing,
    fetchAnalysisType,
    pointPrice,
    pointBasePrice,
    currentPoints,
    namingTicketCount,
    isPointActive,
    isTicketActive,
    popAnalysisType,
    submitPayment,
  };
};
